/**
 * SendDisclosureModal Component
 *
 * Modal for sending initial disclosures to borrowers.
 * Three-tab workflow:
 * 1. Loan Info + Audit Status
 * 2. Document Selection
 * 3. Review & Send
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  XCircle,
  Send,
  FileText,
  User,
  ChevronRight,
  ChevronLeft,
  Loader2,
  RefreshCw,
  History,
  Clock,
  Download
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  disclosureSendService,
  type DisclosureLoanInfo,
  type AuditResult,
  type DocumentOrder,
  type DisclosureDocument,
  type PlanCode,
  type AuditHistoryEntry
} from '@/services/disclosureSendService';
import { useAuth } from '@/contexts/useAuth';
import { type PreValidationResult } from '@/services/disclosurePreValidationService';
import { createComponentLogger } from '@/utils/logger';
import type { DisclosureLoan } from '@/types/disclosure-dashboard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';

const log = createComponentLogger('SendDisclosureModal', 'disclosure-dashboard');

// ============================================
// Types
// ============================================

/**
 * Pre-loaded audit data passed from the dashboard
 * This allows the modal to skip the getCachedAudit API call
 */
interface PreloadedAudit {
  auditId: string;
  auditResult: AuditResult;
  packageType: string;
  planCodeId: string | null;
  isValid: boolean;
}

interface SendDisclosureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Full loan object from the disclosure dashboard */
  loan: DisclosureLoan;
  onSuccess?: () => void;
  /** Pre-validation result from local field check */
  preValidation?: PreValidationResult | null;
  /** Pre-loaded audit data from dashboard (eliminates API call for cached audit) */
  preloadedAudit?: PreloadedAudit | null;
}

type ModalStep = 'loan-info' | 'documents' | 'review';

// ============================================
// Sub-components
// ============================================

/**
 * Warning banner for missing required fields from pre-validation
 */
function PreValidationWarningBanner({ preValidation }: { preValidation: PreValidationResult }) {
  if (preValidation.isComplete) {
return null;
}

  return (
    <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-amber-800 mb-1">
            Missing Required Fields ({preValidation.missingFields.length})
          </div>
          <div className="space-y-1">
            {preValidation.missingFields.map(field => (
              <div key={field.fieldId} className="text-xs text-amber-700 flex items-center gap-2">
                <span className="font-mono bg-amber-100 px-1 rounded">{field.fieldId}</span>
                <span>{field.fieldName}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-amber-600 mt-2">
            You can still proceed. The full Encompass audit will run when you continue.
          </p>
        </div>
      </div>
    </div>
  );
}

function AuditStatusBadge({ status, hasBlockingErrors }: { status: string; hasBlockingErrors?: boolean }) {
  // If there are blocking errors, always show failed regardless of status
  if (hasBlockingErrors) {
    return (
      <Badge className="bg-red-100 text-red-800">
        <XCircle className="h-3 w-3 mr-1" />
        Has Errors
      </Badge>
    );
  }

  switch (status) {
    case 'SUCCEEDED':
      return (
        <Badge className="bg-green-100 text-green-800">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Passed
        </Badge>
      );
    case 'WARNING':
      return (
        <Badge className="bg-amber-100 text-amber-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Passed with Warnings
        </Badge>
      );
    case 'FAILED':
      return (
        <Badge className="bg-red-100 text-red-800">
          <XCircle className="h-3 w-3 mr-1" />
          Failed
        </Badge>
      );
    case 'RUNNING':
      return (
        <Badge variant="outline">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Running
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function DocumentList({
  documents,
  selectedIds,
  onToggle
}: {
  documents: DisclosureDocument[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
}) {
  return (
    <div className="space-y-1">
      {documents.map(doc => (
        <div
          key={doc.id}
          className={cn(
            'flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 cursor-pointer transition-colors',
            selectedIds.has(doc.id) && 'bg-muted/30'
          )}
          onClick={() => onToggle(doc.id)}
        >
          <Checkbox
            checked={selectedIds.has(doc.id)}
            onCheckedChange={() => onToggle(doc.id)}
          />
          <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm break-words">{doc.name}</div>
          </div>
          {doc.signatureType && (
            <Badge
              variant="outline"
              className={cn(
                'text-xs flex-shrink-0',
                doc.signatureType === 'eSignable' && 'bg-blue-50 text-blue-700 border-blue-200'
              )}
            >
              {doc.signatureType === 'eSignable' ? 'eSign' : doc.signatureType}
            </Badge>
          )}
        </div>
      ))}
    </div>
  );
}

// ============================================
// Main Component
// ============================================

// Format currency helper
function formatCurrency(amount: number | null): string {
  if (amount === null) {
return '-';
}
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

// Format Encompass error messages for user-friendly display
function formatEncompassError(error: string): string {
  // Check for loan lock pattern
  const lockMatch = error.match(/locked by (?:another )?user '([^']+)'/i);
  if (lockMatch) {
    return `Loan is locked by user '${lockMatch[1]}'. Please have them close the loan and try again.`;
  }
  return error;
}

const STALE_DISCLOSURE_RECIPIENT_CODE = 'stale_disclosure_recipient';
const STALE_DISCLOSURE_RECIPIENT_MESSAGE =
  'Disclosure recipients changed. Regenerate the disclosure package before sending.';

function normalizeDisclosureRecipientEmail(email: string | null | undefined): string | null {
  const normalized = email?.trim().toLowerCase();
  return normalized ? normalized : null;
}

export function SendDisclosureModal({
  open,
  onOpenChange,
  loan,
  onSuccess,
  preValidation,
  preloadedAudit
}: SendDisclosureModalProps) {
  // Extract commonly used values from the loan object
  const encompassLoanId = loan.encompass_loan_id;
  const loanNumber = loan.trans_details_loan_number || 'Unknown';
  const borrowerName = loan.borrower_name;
  // State
  const [step, setStep] = useState<ModalStep>('loan-info');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [loanInfo, setLoanInfo] = useState<DisclosureLoanInfo | null>(null);
  const [auditResult, setAuditResult] = useState<AuditResult | null>(null);
  const [documentOrder, setDocumentOrder] = useState<DocumentOrder | null>(null);
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());

  // Package Type state (AtApp = At Application, 3Day = 3-Day Package for TRID/GFCRAJ)
  const [packageType, setPackageType] = useState<'AtApp' | '3Day'>('AtApp');

  // Plan Code state
  const [planCodes, setPlanCodes] = useState<PlanCode[]>([]);
  const [, setShowPlanCodeSelect] = useState(false);
  const [selectedPlanCodeId, setSelectedPlanCodeId] = useState<string | null>(null);
  const [applyingPlanCode, setApplyingPlanCode] = useState(false);
  const [editingPlanCode, setEditingPlanCode] = useState(false);

  // Sending state
  const [sending, setSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [staleRecipientEmails, setStaleRecipientEmails] = useState<string[]>([]);

  // Preview auto-generation state
  const [previewState, setPreviewState] = useState<{
    blobUrl: string | null;
    generating: boolean;
    selectionHash: string | null;
    error: string | null;
    failures: Array<{ documentId: string; error: string }> | null;
  }>({ blobUrl: null, generating: false, selectionHash: null, error: null, failures: null });

  // Helper to create a hash of current selection for change detection
  const getSelectionHash = useCallback(() => {
    return Array.from(selectedDocIds).sort().join(',');
  }, [selectedDocIds]);

  // Audit cache - track what parameters the audit was run for
  const [auditCacheKey, setAuditCacheKey] = useState<string | null>(null);
  const [isAuditFromCache, setIsAuditFromCache] = useState(false);
  const [auditHistory, setAuditHistory] = useState<AuditHistoryEntry[]>([]);
  const [showAuditHistory, setShowAuditHistory] = useState(false);

  // Get auth context for organization and user IDs
  const { user } = useAuth();

  const currentDisclosureRecipientEmailSet = useMemo(() => {
    return new Set(
      (loanInfo?.currentDisclosureRecipientEmails || [])
        .map(normalizeDisclosureRecipientEmail)
        .filter((email): email is string => Boolean(email))
    );
  }, [loanInfo?.currentDisclosureRecipientEmails]);

  // Helper to generate cache key for audit results
  const getAuditCacheKey = useCallback(
    (pkgType: string, planCodeId: string | null) =>
      `${encompassLoanId}-${pkgType}-${planCodeId || 'none'}`,
    [encompassLoanId]
  );

  // Refs to access current audit values without triggering effect re-runs
  // This breaks the dependency cycle in loadLoanInfo
  const auditResultRef = useRef<AuditResult | null>(null);
  const auditCacheKeyRef = useRef<string | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setStep('loan-info');
      setLoading(false);
      setError(null);
      setLoanInfo(null);
      setAuditResult(null);
      setAuditCacheKey(null);
      setIsAuditFromCache(false);
      setAuditHistory([]);
      setShowAuditHistory(false);
      setDocumentOrder(null);
      setSelectedDocIds(new Set());
      setPackageType('AtApp');
      setPlanCodes([]);
      setShowPlanCodeSelect(false);
      setSelectedPlanCodeId(null);
      setApplyingPlanCode(false);
      setEditingPlanCode(false);
      setSending(false);
      setSendSuccess(false);
      setStaleRecipientEmails([]);
      setPreviewState({ blobUrl: null, generating: false, selectionHash: null, error: null, failures: null });
      setCachedPreviewUrl(null);
      setCachedPreviewHash(null);
    }
  }, [open]);

  // Keep refs in sync with state (breaks the dependency cycle)
  useEffect(() => {
    auditResultRef.current = auditResult;
  }, [auditResult]);

  useEffect(() => {
    auditCacheKeyRef.current = auditCacheKey;
  }, [auditCacheKey]);

  // Run audit for a loan (defined first as loadLoanInfo depends on it)
  const runAudit = useCallback(async (
    loan: DisclosureLoanInfo,
    selectedPackageType: 'AtApp' | '3Day' = 'AtApp',
    currentPlanCodeId: string | null = null,
    force = false
  ) => {
    if (!loan.borrowerPairId) {
      return;
    }

    setLoading(true);
    setError(null);
    setIsAuditFromCache(false);

    try {
      log.info('Running audit', {
        encompassLoanId,
        borrowerPairId: loan.borrowerPairId,
        packageType: selectedPackageType,
        planCodeId: currentPlanCodeId,
        force
      });

      const audit = await disclosureSendService.createAudit(
        encompassLoanId,
        loan.borrowerPairId,
        [selectedPackageType],
        {
          organizationId: user?.organization_id,
          userId: user?.id,
          planCodeId: currentPlanCodeId
        }
      );

      setAuditResult(audit);

      // Cache the audit key on success so we can skip re-running for same parameters
      if (audit.success) {
        const cacheKey = getAuditCacheKey(selectedPackageType, currentPlanCodeId);
        setAuditCacheKey(cacheKey);
        log.info('Audit cached', { auditId: audit.auditId, cacheKey });
      }

      if (!audit.success) {
        if (audit.hasBlockingErrors) {
          setError('Cannot proceed: The loan has required data errors that must be fixed in Encompass before sending disclosures.');
        } else {
          setError('Audit failed. Please review the errors below.');
        }
      }

      // Refresh audit history after running a new audit
      if (user?.organization_id) {
        const history = await disclosureSendService.getAuditHistory(encompassLoanId, 10);
        setAuditHistory(history);
      }
    } catch (err) {
      log.error('Error running audit', { encompassLoanId }, err as Error);
      setError((err as Error).message || 'Failed to run audit');
    } finally {
      setLoading(false);
    }
  }, [encompassLoanId, getAuditCacheKey, user?.organization_id, user?.id]);

  // Load loan info when modal opens
  const loadLoanInfo = useCallback(async () => {
    if (!open || !encompassLoanId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1: Parallel fetch loan info, plan codes, and audit history
      log.info('Loading loan info (parallel)', { encompassLoanId });
      const [loanData, codes, history] = await Promise.all([
        disclosureSendService.getLoanInfo(encompassLoanId),
        disclosureSendService.getPlanCodes('opening'),
        user?.organization_id
          ? disclosureSendService.getAuditHistory(encompassLoanId, 10)
          : Promise.resolve([])
      ]);

      if (!loanData) {
        setError('Failed to load loan information');
        setLoading(false);
        return;
      }

      if (!loanData.borrowerPairId) {
        setError('Loan is missing borrower pair ID. Cannot send disclosures.');
        setLoading(false);
        return;
      }

      setLoanInfo(loanData);
      setPlanCodes(codes);
      setAuditHistory(history);
      log.info('Loaded data in parallel', {
        hasLoan: !!loanData,
        planCodeCount: codes.length,
        historyCount: history.length
      });

      // Pre-select current plan code if one exists
      if (loanData.planCode?.id) {
        setSelectedPlanCodeId(loanData.planCode.id);
        log.info('Current plan code', { planCode: loanData.planCode });

        // Check if we have a cached audit for this combination
        // Use refs to avoid dependency cycle (auditResult/auditCacheKey in deps would cause infinite loop)
        const cacheKey = getAuditCacheKey(packageType, loanData.planCode.id);
        if (auditResultRef.current?.success && auditCacheKeyRef.current === cacheKey) {
          log.info('Reusing in-memory cached audit', { auditId: auditResultRef.current.auditId, cacheKey });
          // Skip audit, already have valid result
        } else if (
          preloadedAudit?.isValid &&
          preloadedAudit.packageType === packageType &&
          preloadedAudit.planCodeId === loanData.planCode.id
        ) {
          // Use preloaded audit from dashboard - instant load!
          log.info('Using preloaded audit from dashboard', {
            auditId: preloadedAudit.auditId,
            packageType: preloadedAudit.packageType
          });
          setAuditResult(preloadedAudit.auditResult);
          setAuditCacheKey(cacheKey);
          setIsAuditFromCache(true);
        } else {
          // Check for a valid cached audit in the database (must match package type AND plan code)
          const cached = await disclosureSendService.getCachedAudit(
            encompassLoanId,
            packageType,
            loanData.planCode?.id || null
          );

          if (cached.cached && cached.auditResult) {
            log.info('Using database cached audit', {
              auditId: cached.auditId,
              cachedAt: cached.cachedAt
            });
            setAuditResult(cached.auditResult);
            setAuditCacheKey(cacheKey);
            setIsAuditFromCache(true);
          } else {
            // No valid cache - user will click "Run Audit" button manually
            log.info('No cached audit available');
          }
        }
      } else {
        // No plan code - user must select one
        log.info('No plan code applied');
        setShowPlanCodeSelect(true);
      }
    } catch (err) {
      log.error('Error loading loan info', { encompassLoanId }, err as Error);
      setError((err as Error).message || 'Failed to load loan data');
    } finally {
      setLoading(false);
    }
  // Note: auditResult and auditCacheKey are accessed via refs to avoid dependency cycle
  }, [open, encompassLoanId, packageType, getAuditCacheKey, user?.organization_id, preloadedAudit]);

  // Apply selected plan code
  const applyPlanCode = useCallback(async () => {
    if (!loanInfo || !selectedPlanCodeId) {
return;
}

    setApplyingPlanCode(true);
    setError(null);

    try {
      log.info('Applying plan code', { loanId: encompassLoanId, planCodeId: selectedPlanCodeId });
      const result = await disclosureSendService.applyPlanCode(
        encompassLoanId,
        selectedPlanCodeId,
        'opening',
        'all'
      );

      if (result.success) {
        // Refresh loan info to get updated plan code
        const updatedLoan = await disclosureSendService.getLoanInfo(encompassLoanId);
        if (updatedLoan) {
          setLoanInfo(updatedLoan);
        }
        setShowPlanCodeSelect(false);
        setEditingPlanCode(false);
        // Run audit after plan code is applied with selected package type
        await runAudit(updatedLoan || loanInfo, packageType, selectedPlanCodeId);
      } else {
        setError(result.error || `Failed to apply plan code: ${result.conflicts.length} conflicts found`);
      }
    } catch (err) {
      log.error('Error applying plan code', { encompassLoanId }, err as Error);
      setError((err as Error).message || 'Failed to apply plan code');
    } finally {
      setApplyingPlanCode(false);
    }
  }, [loanInfo, selectedPlanCodeId, encompassLoanId, runAudit, packageType]);

  useEffect(() => {
    loadLoanInfo();
  }, [loadLoanInfo]);

  // Cached document order data (for preview URL)
  const [cachedPreviewUrl, setCachedPreviewUrl] = useState<string | null>(null);
  const [cachedPreviewHash, setCachedPreviewHash] = useState<string | null>(null);

  // Generate document set when moving to documents tab
  // Pass loanId to auto-add required state-specific forms (e.g., CA disclosure forms)
  const generateDocSet = useCallback(async (
    forceFresh = false,
    borrowerPairIdOverride?: string | null
  ) => {
    if (!auditResult?.auditId || (!forceFresh && documentOrder)) {
return;
}

    setLoading(true);
    setError(null);
    setStaleRecipientEmails([]);
    try {
      const borrowerPairId =
        borrowerPairIdOverride !== undefined
          ? borrowerPairIdOverride || undefined
          : loanInfo?.borrowerPairId || undefined;

      // First, check if we have a valid cached document order
      log.info('Checking for cached document order', { encompassLoanId, forceFresh });
      const cached = forceFresh
        ? { cached: false }
        : await disclosureSendService.getCachedDocumentOrder(encompassLoanId);

      if (cached.cached && cached.orderId && cached.documents) {
        log.info('Using cached document order', {
          orderId: cached.orderId,
          docCount: cached.documents.length,
          hasPreview: !!cached.previewUrl
        });

        // Filter out "Removed" documents
        const selectableDocs = cached.documents.filter(d => d.difference !== 'Removed');

        setDocumentOrder({
          success: true,
          orderId: cached.orderId,
          status: 'SUCCEEDED',
          documents: selectableDocs,
          parties: cached.parties || [],
          borrowerPairId
        });
        // Select all selectable documents by default
        setSelectedDocIds(new Set(selectableDocs.map(d => d.id).filter(Boolean)));

        // Store cached preview URL for later use
        if (cached.previewUrl) {
          setCachedPreviewUrl(cached.previewUrl);
          setCachedPreviewHash(cached.previewHash || null);
        }

        setLoading(false);
        return;
      }

      // No cache - generate fresh document order
      log.info('Generating document set', { auditId: auditResult.auditId, encompassLoanId, borrowerPairId });
      // Pass encompassLoanId and borrowerPairId to auto-add required forms based on state (e.g., CA forms)
      const result = await disclosureSendService.generateDocSet(
        auditResult.auditId,
        encompassLoanId,
        borrowerPairId
      );

      if (result.order) {
        // Filter out "Removed" documents - they have no ID and can't be sent
        const allDocs = result.order.documents || [];
        const selectableDocs = allDocs.filter(d => d.difference !== 'Removed');

        // Log if any docs filtered for debugging
        const removedCount = allDocs.length - selectableDocs.length;
        if (removedCount > 0) {
          log.info('Filtered removed documents', {
            total: allDocs.length,
            selectable: selectableDocs.length,
            removed: removedCount
          });
        }

        // Warn if a doc has no ID but isn't marked Removed (unexpected)
        const unexpectedMissingId = selectableDocs.filter(d => !d.id);
        if (unexpectedMissingId.length > 0) {
          log.warn('Documents missing ID but not marked Removed', {
            count: unexpectedMissingId.length,
            names: unexpectedMissingId.map(d => d.name)
          });
        }

        setDocumentOrder({
          ...result.order,
          documents: selectableDocs
        });
        // Select all selectable documents by default
        setSelectedDocIds(new Set(selectableDocs.map(d => d.id).filter(Boolean)));
      } else {
        setError(result.error || 'Failed to generate document set');
      }
    } catch (err) {
      log.error('Error generating doc set', {}, err as Error);
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [auditResult?.auditId, documentOrder, encompassLoanId, loanInfo?.borrowerPairId]);

  const regenerateDisclosurePackage = useCallback(async () => {
    const updatedLoan = await disclosureSendService.getLoanInfo(encompassLoanId);
    if (updatedLoan) {
      setLoanInfo(updatedLoan);
    }

    setDocumentOrder(null);
    setSelectedDocIds(new Set());
    setCachedPreviewUrl(null);
    setCachedPreviewHash(null);
    setPreviewState({ blobUrl: null, generating: false, selectionHash: null, error: null, failures: null });
    setStaleRecipientEmails([]);
    setStep('documents');
    await generateDocSet(true, updatedLoan?.borrowerPairId);
  }, [encompassLoanId, generateDocSet]);

  // Handle step navigation
  const goToDocuments = async () => {
    setStep('documents');
    await generateDocSet();
  };

  const goToReview = () => {
    if (selectedDocIds.size === 0) {
      setError('Please select at least one document to send');
      return;
    }
    setError(null);
    setStep('review');
  };

  // Toggle document selection
  const toggleDocument = (docId: string) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  };

  // Select/deselect all
  const toggleSelectAll = () => {
    if (selectedDocIds.size === documentOrder?.documents?.length) {
      setSelectedDocIds(new Set());
    } else {
      setSelectedDocIds(new Set(documentOrder?.documents?.map(d => d.id) || []));
    }
  };

  // Generate preview PDF in background (called automatically when entering Review tab)
  const generatePreviewInBackground = useCallback(async () => {
    // Need orderId and documents to generate preview
    if (!encompassLoanId || !documentOrder?.orderId || !documentOrder?.documents?.length) {
      log.info('Preview generation skipped - missing required data', {
        hasLoanId: !!encompassLoanId,
        hasOrderId: !!documentOrder?.orderId,
        docCount: documentOrder?.documents?.length || 0
      });
      return;
    }

    const currentHash = getSelectionHash();

    // Skip if already generating or already have this selection's preview
    if (previewState.generating || previewState.selectionHash === currentHash) {
      log.info('Preview generation skipped', {
        generating: previewState.generating,
        hashMatch: previewState.selectionHash === currentHash
      });
      return;
    }

    setPreviewState(prev => ({ ...prev, generating: true, error: null, failures: null }));

    try {
      // Get ONLY selected document IDs (not all documents)
      const selectedDocIdArray = Array.from(selectedDocIds);

      if (selectedDocIdArray.length === 0) {
        setPreviewState(prev => ({
          ...prev,
          generating: false,
          error: 'No documents selected for preview'
        }));
        return;
      }

      // First, check for cached preview if we have all documents selected (default selection)
      if (cachedPreviewUrl && cachedPreviewHash) {
        log.info('Checking cached preview', {
          cachedHash: cachedPreviewHash.substring(0, 20),
          currentHash: currentHash.substring(0, 20),
          hasCachedUrl: !!cachedPreviewUrl
        });

        const cachedResult = await disclosureSendService.getCachedPreviewPdf(
          cachedPreviewUrl,
          currentHash,
          cachedPreviewHash
        );

        if (cachedResult.success && cachedResult.blobUrl) {
          setPreviewState({
            blobUrl: cachedResult.blobUrl,
            generating: false,
            selectionHash: currentHash,
            error: null,
            failures: null
          });
          log.info('Using cached preview PDF - instant load');
          return;
        }

        log.info('Cached preview not valid, generating fresh', { reason: cachedResult.error });
      }

      // No valid cache - generate preview on-demand
      log.info('Generating preview PDF in background', {
        orderId: documentOrder.orderId,
        loanId: encompassLoanId,
        documentCount: selectedDocIdArray.length,
        selectionHash: currentHash
      });

      const result = await disclosureSendService.getPreviewPdf(
        documentOrder.orderId,
        selectedDocIdArray,
        encompassLoanId
      );

      if (result.success && result.blobUrl) {
        setPreviewState({
          blobUrl: result.blobUrl,
          generating: false,
          selectionHash: currentHash,
          error: null,
          failures: result.failures || null
        });
        log.info('Preview generated successfully', {
          failureCount: result.failures?.length || 0
        });
      } else {
        setPreviewState(prev => ({
          ...prev,
          generating: false,
          error: result.error || 'Failed to generate preview',
          failures: null
        }));
      }
    } catch (err) {
      log.error('Error generating preview', {}, err as Error);
      setPreviewState(prev => ({
        ...prev,
        generating: false,
        error: (err as Error).message || 'Failed to generate preview',
        failures: null
      }));
    }
  }, [encompassLoanId, documentOrder?.orderId, documentOrder?.documents?.length, selectedDocIds, getSelectionHash, previewState.generating, previewState.selectionHash, cachedPreviewUrl, cachedPreviewHash]);

  // Handle download preview button click
  const handleDownloadPreview = () => {
    if (previewState.blobUrl) {
      // Preview ready - instant open
      window.open(previewState.blobUrl, '_blank');
      log.info('Preview opened from cache');
    } else if (previewState.generating) {
      // Still generating - nothing to do, button shows spinner
      log.info('Preview still generating...');
    } else {
      // No preview yet - trigger generation (fallback)
      generatePreviewInBackground();
    }
  };

  // Auto-generate preview when entering Review tab or when selection changes while on Review
  useEffect(() => {
    if (step === 'review' && documentOrder?.documents?.length) {
      const currentHash = getSelectionHash();
      // Only generate if we don't have a preview for this selection and not already generating
      if (previewState.selectionHash !== currentHash && !previewState.generating) {
        log.info('Auto-triggering preview generation', { step, currentHash, previousHash: previewState.selectionHash });
        generatePreviewInBackground();
      }
    }
  }, [step, documentOrder?.documents?.length, getSelectionHash, previewState.selectionHash, previewState.generating, generatePreviewInBackground]);

  // Send disclosures
  const handleSend = async () => {
    if (!loanInfo || !documentOrder || selectedDocIds.size === 0) {
return;
}

    setSending(true);
    setError(null);
    setStaleRecipientEmails([]);

    try {
      // Get borrower party from the order
      const borrowerParty = documentOrder.parties?.find(p => p.type === 'Borrower');

      if (!borrowerParty) {
        setError('No borrower found in the disclosure package');
        setSending(false);
        return;
      }

      // Build from/to objects
      const from = {
        fullName: loanInfo.loanOfficer.name || 'Lendwise Mortgage',
        emailAddress: loanInfo.loanOfficer.email || 'disclosures@lendwisemtg.com'
      };

      const to = {
        id: borrowerParty.id,
        fullName: borrowerParty.fullName,
        emailAddress: borrowerParty.contact?.email || loanInfo.borrower.email || ''
      };

      if (!to.emailAddress) {
        setError('Borrower email address is required');
        setSending(false);
        return;
      }

      const normalizedRecipientEmail = normalizeDisclosureRecipientEmail(to.emailAddress);
      if (
        normalizedRecipientEmail &&
        currentDisclosureRecipientEmailSet.size > 0 &&
        !currentDisclosureRecipientEmailSet.has(normalizedRecipientEmail)
      ) {
        setStaleRecipientEmails([normalizedRecipientEmail]);
        setError(STALE_DISCLOSURE_RECIPIENT_MESSAGE);
        setSending(false);
        return;
      }

      log.info('Sending disclosures', {
        loanId: encompassLoanId,
        orderId: documentOrder.orderId,
        docCount: selectedDocIds.size
      });

      const result = await disclosureSendService.sendPackage(
        encompassLoanId,
        documentOrder.orderId,
        Array.from(selectedDocIds),
        from,
        to
      );

      if (result.success) {
        setSendSuccess(true);
        log.info('Disclosures sent successfully', { encompassLoanId });
        // Call success callback after a brief delay
        setTimeout(() => {
          onSuccess?.();
          onOpenChange(false);
        }, 2000);
      } else if (result.code === STALE_DISCLOSURE_RECIPIENT_CODE) {
        const staleEmails = result.staleEmails?.length
          ? result.staleEmails
          : normalizedRecipientEmail
            ? [normalizedRecipientEmail]
            : [];
        setStaleRecipientEmails(staleEmails);
        setError(result.message || result.error || STALE_DISCLOSURE_RECIPIENT_MESSAGE);
      } else {
        setError(result.error || 'Failed to send disclosures');
      }
    } catch (err) {
      log.error('Error sending disclosures', {}, err as Error);
      setError((err as Error).message);
    } finally {
      setSending(false);
    }
  };

  // Render content based on step
  const renderLoanInfo = () => (
    <div className="space-y-6">
      {/* Pre-validation Warning Banner */}
      {preValidation && !preValidation.isComplete && (
        <PreValidationWarningBanner preValidation={preValidation} />
      )}

      {/* Loan Info */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Loan Information
        </h3>
        <div className="grid grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg">
          {/* Row 1: Borrower & Co-Borrower */}
          <div>
            <div className="text-xs text-muted-foreground">Borrower</div>
            <div className="font-medium">{loan.borrower_name}</div>
            {loanInfo?.borrower.email && (
              <div className="text-sm text-muted-foreground">{loanInfo.borrower.email}</div>
            )}
          </div>
          {loan.coborrower_name && (
            <div>
              <div className="text-xs text-muted-foreground">Co-Borrower</div>
              <div className="font-medium">{loan.coborrower_name}</div>
              {loanInfo?.coborrower?.email && (
                <div className="text-sm text-muted-foreground">{loanInfo.coborrower.email}</div>
              )}
            </div>
          )}

          {/* Row 2: Loan Officer & Amount */}
          <div>
            <div className="text-xs text-muted-foreground">Loan Officer</div>
            <div className="font-medium">{loan.loan_officer_name}</div>
            {loanInfo?.loanOfficer.email && (
              <div className="text-sm text-muted-foreground">{loanInfo.loanOfficer.email}</div>
            )}
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Loan Amount</div>
            <div className="font-medium">{formatCurrency(loan.trans_details_loan_amt)}</div>
          </div>

          {/* Row 3: Loan Type & Amortization */}
          <div>
            <div className="text-xs text-muted-foreground">Loan Type</div>
            <div className="font-medium">{loan.trans_details_loan_type || '-'}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Amortization</div>
            <div className="font-medium">{loan.amortization_type || '-'}</div>
          </div>

          {/* Row 4: Rate & Term */}
          <div>
            <div className="text-xs text-muted-foreground">Interest Rate</div>
            <div className="font-medium">{loan.trans_details_interest_rate ? `${loan.trans_details_interest_rate}%` : '-'}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Term</div>
            <div className="font-medium">{loan.loan_term_months ? `${loan.loan_term_months} months` : '-'}</div>
          </div>

          {/* Row 5: Interest Only (if applicable) */}
          {loan.interest_only_term_months && loan.interest_only_term_months > 0 && (
            <div>
              <div className="text-xs text-muted-foreground">Interest Only</div>
              <div className="font-medium">{loan.interest_only_term_months} months</div>
            </div>
          )}
        </div>
      </div>

      {/* Package Type Section */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Package Type
        </h3>
        <div className="p-4 bg-muted/30 rounded-lg">
          <div className="flex items-center gap-3">
            <Select
              value={packageType}
              onValueChange={(value: 'AtApp' | '3Day') => {
                setPackageType(value);
                // Check if we have a cached audit for the new package type
                const newCacheKey = getAuditCacheKey(value, selectedPlanCodeId);
                // Only clear if we don't have a cached audit for this combination
                if (auditCacheKey !== newCacheKey) {
                  setAuditResult(null);
                  setDocumentOrder(null);
                  setSelectedDocIds(new Set());
                  log.info('Package type changed, clearing audit and documents', { newPackageType: value, cacheKey: auditCacheKey, newCacheKey });
                } else {
                  log.info('Package type changed but have cached audit', { newPackageType: value, cacheKey: newCacheKey });
                }
              }}
              disabled={loading || applyingPlanCode}
            >
              <SelectTrigger className="flex-1 bg-white">
                <SelectValue placeholder="Select package type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AtApp">
                  <div className="flex flex-col">
                    <span>At Application</span>
                    <span className="text-xs text-muted-foreground">Standard initial disclosures</span>
                  </div>
                </SelectItem>
                <SelectItem value="3Day">
                  <div className="flex flex-col">
                    <span>3-Day Package</span>
                    <span className="text-xs text-muted-foreground">TRID Rule disclosures (includes GFCRAJ)</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {packageType === 'AtApp'
              ? 'At Application disclosures are sent at the start of the loan process.'
              : '3-Day packages include Fair Credit Reporting Act (GFCRAJ) and other TRID-required documents.'}
          </p>
        </div>
      </div>

      {/* Plan Code Section */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Plan Code
        </h3>
        {loading && !loanInfo ? (
          <Skeleton className="h-16 w-full" />
        ) : planCodes.length > 0 ? (
          <>
            {/* Display mode: Show plan code with Change button */}
            {loanInfo?.planCode && !editingPlanCode ? (
              <div className="p-4 rounded-lg border border-green-200 bg-green-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <div>
                      <div className="font-medium text-green-900">{loanInfo.planCode.id}</div>
                      <div className="text-sm text-green-700">{loanInfo.planCode.description}</div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingPlanCode(true)}
                  >
                    Change
                  </Button>
                </div>
              </div>
            ) : (
              /* Edit mode: Show dropdown selector */
              <div className={cn(
                "p-4 rounded-lg space-y-3",
                !loanInfo?.planCode
                  ? "border border-amber-200 bg-amber-50"
                  : "border border-border bg-muted/30"
              )}>
                {!loanInfo?.planCode && (
                  <div className="flex items-start gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      No plan code is applied to this loan. Select a plan code to continue.
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <Select
                    value={selectedPlanCodeId || ''}
                    onValueChange={(value) => {
                      setSelectedPlanCodeId(value);
                      // Clear audit and document order if plan code changed from current
                      if (loanInfo?.planCode?.id && value !== loanInfo.planCode.id) {
                        setAuditResult(null);
                        setDocumentOrder(null);
                        setSelectedDocIds(new Set());
                      }
                    }}
                    disabled={applyingPlanCode}
                  >
                    <SelectTrigger className="flex-1 bg-white">
                      <SelectValue placeholder="Select a plan code..." />
                    </SelectTrigger>
                    <SelectContent>
                      {planCodes.map(pc => (
                        <SelectItem key={pc.code} value={pc.code}>
                          <div className="flex flex-col">
                            <span>{pc.code} - {pc.description}</span>
                            {pc.investorName && (
                              <span className="text-xs text-muted-foreground">{pc.investorName}</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    onClick={applyPlanCode}
                    disabled={!selectedPlanCodeId || applyingPlanCode || (selectedPlanCodeId === loanInfo?.planCode?.id && auditResult !== null)}
                  >
                    {applyingPlanCode ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Applying...
                      </>
                    ) : selectedPlanCodeId === loanInfo?.planCode?.id && auditResult !== null ? (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        Applied
                      </>
                    ) : (
                      'Apply'
                    )}
                  </Button>
                  {editingPlanCode && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingPlanCode(false);
                        // Reset selection to current plan code
                        setSelectedPlanCodeId(loanInfo?.planCode?.id || null);
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
                {loanInfo?.planCode && selectedPlanCodeId !== loanInfo.planCode.id && (
                  <div className="text-xs text-muted-foreground">
                    Changing plan code will re-run the audit
                  </div>
                )}
              </div>
            )}
          </>
        ) : null}
      </div>

      {/* Audit Status */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Audit Status
        </h3>
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : !auditResult && loanInfo?.planCode?.id ? (
          // Plan code exists but no audit result (e.g., after package type change)
          <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-800">
                  Audit needs to be run for <strong>{packageType === 'AtApp' ? 'At Application' : '3-Day Package'}</strong> disclosures.
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => runAudit(loanInfo, packageType, loanInfo.planCode?.id || null)}
                disabled={loading}
              >
                Run Audit
              </Button>
            </div>
          </div>
        ) : auditResult ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AuditStatusBadge status={auditResult.status} hasBlockingErrors={auditResult.hasBlockingErrors} />
                {auditResult.messages && auditResult.messages.length > 0 && (
                  <span className="text-sm text-muted-foreground">
                    ({auditResult.messages.length} message{auditResult.messages.length !== 1 ? 's' : ''})
                  </span>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setAuditResult(null);
                  setIsAuditFromCache(false);
                  setDocumentOrder(null);
                  setSelectedDocIds(new Set());
                  if (loanInfo) {
                    runAudit(loanInfo, packageType, loanInfo.planCode?.id || null, true);
                  }
                }}
                disabled={loading}
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Re-run Audit
              </Button>
            </div>

            {/* Audit ID and Cache Status */}
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <span className="font-medium">Audit ID:</span>
                <code className="bg-muted px-1.5 py-0.5 rounded font-mono text-[10px]">
                  {auditResult.auditId}
                </code>
              </div>
              {isAuditFromCache && auditResult.cachedAt && (
                <div className="flex items-center gap-1 text-green-600">
                  <Clock className="h-3 w-3" />
                  <span>Cached {new Date(auditResult.cachedAt).toLocaleString()}</span>
                </div>
              )}
              {auditHistory.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAuditHistory(!showAuditHistory)}
                  className="flex items-center gap-1 hover:text-foreground transition-colors"
                >
                  <History className="h-3 w-3" />
                  <span>{showAuditHistory ? 'Hide' : 'View'} History ({auditHistory.length})</span>
                </button>
              )}
            </div>

            {/* Audit History (collapsible) */}
            {showAuditHistory && auditHistory.length > 0 && (
              <div className="border rounded-md p-2 bg-muted/30">
                <div className="text-xs font-medium text-muted-foreground mb-2">Past Audits</div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {auditHistory.map((audit) => (
                    <div
                      key={audit.id}
                      className="flex items-center justify-between text-xs py-1 border-b border-muted last:border-0"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">
                          {new Date(audit.created_at).toLocaleString()}
                        </span>
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-[10px] px-1.5 py-0',
                            audit.status === 'SUCCEEDED' && 'bg-green-50 text-green-700 border-green-200',
                            audit.status === 'WARNING' && 'bg-amber-50 text-amber-700 border-amber-200',
                            audit.status === 'FAILED' && 'bg-red-50 text-red-700 border-red-200'
                          )}
                        >
                          {audit.status}
                        </Badge>
                        <span className="text-muted-foreground">
                          {audit.package_type}
                        </span>
                      </div>
                      <code className="text-[10px] font-mono text-muted-foreground">
                        {audit.audit_id.substring(0, 8)}...
                      </code>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Audit Messages */}
            {auditResult.messages && auditResult.messages.length > 0 && (
              <ScrollArea className="h-48 border rounded-md p-3">
                <div className="space-y-2">
                  {auditResult.messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={cn(
                        'flex items-start gap-2 p-2 rounded text-sm',
                        msg.severity === 'Error' && 'bg-red-50 text-red-800',
                        msg.severity === 'Warning' && 'bg-amber-50 text-amber-800',
                        msg.severity === 'Info' && 'bg-blue-50 text-blue-800'
                      )}
                    >
                      {msg.severity === 'Error' && <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />}
                      {msg.severity === 'Warning' && <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />}
                      {msg.severity === 'Info' && <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />}
                      <div>
                        <div>{msg.description}</div>
                        {msg.fields && msg.fields.length > 0 && (
                          <div className="text-xs mt-1 opacity-70">
                            Fields: {msg.fields.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}

          </div>
        ) : null}
      </div>
    </div>
  );

  const renderDocuments = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Documents ({documentOrder?.documents?.length || 0})
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleSelectAll}
          className="text-xs"
        >
          {selectedDocIds.size === documentOrder?.documents?.length ? 'Deselect All' : 'Select All'}
        </Button>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : documentOrder?.documents && documentOrder.documents.length > 0 ? (
        <ScrollArea className="h-[350px] border rounded-md p-2">
          <DocumentList
            documents={documentOrder.documents}
            selectedIds={selectedDocIds}
            onToggle={toggleDocument}
          />
        </ScrollArea>
      ) : documentOrder?.documents && documentOrder.documents.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-[350px] text-muted-foreground border rounded-md">
          <AlertCircle className="h-8 w-8 mb-2" />
          <p className="text-sm">No selectable documents available.</p>
          <p className="text-xs mt-1">All documents in this package have been removed.</p>
        </div>
      ) : error ? (
        // Show error with retry button when document generation fails
        <div className="flex flex-col items-center justify-center py-12 px-4 border rounded-md bg-muted/30">
          <AlertTriangle className="h-10 w-10 text-amber-500 mb-4" />
          <div className="text-center space-y-2 mb-4">
            <h4 className="font-medium text-foreground">Document Generation Failed</h4>
            <p className="text-sm text-muted-foreground max-w-md">
              {formatEncompassError(error)}
            </p>
          </div>
          <Button
            onClick={() => {
              setError(null);
              setDocumentOrder(null);
              generateDocSet();
            }}
            variant="outline"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry Generation
          </Button>
        </div>
      ) : null}

      <div className="text-sm text-muted-foreground">
        {selectedDocIds.size} of {documentOrder?.documents?.length || 0} documents selected
      </div>
    </div>
  );

  const renderReview = () => {
    const selectedDocs = documentOrder?.documents?.filter(d => selectedDocIds.has(d.id)) || [];
    const borrowerParty = documentOrder?.parties?.find(p => p.type === 'Borrower');

    return (
      <div className="space-y-6">
        {/* Recipient */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Recipient
          </h3>
          <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-lg">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="font-medium">{borrowerParty?.fullName || loanInfo?.borrower.name}</div>
              <div className="text-sm text-muted-foreground">
                {borrowerParty?.contact?.email || loanInfo?.borrower.email}
              </div>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Will receive email with link to Consumer Connect signing portal
          </p>
        </div>

        {/* Documents Summary */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Documents ({selectedDocs.length})
          </h3>
          <ScrollArea className="h-32 border rounded-md p-3">
            <div className="space-y-1 text-sm">
              {selectedDocs.map(doc => (
                <div key={doc.id} className="flex items-center gap-2 min-w-0">
                  <FileText className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                  <span className="break-words">{doc.name}</span>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Download Preview */}
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleDownloadPreview}
            disabled={!documentOrder?.orderId || (!previewState.blobUrl && previewState.generating)}
          >
            {previewState.generating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating Preview...
              </>
            ) : previewState.blobUrl ? (
              <>
                <Download className="h-4 w-4 mr-2" />
                Download Preview PDF
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Generate Preview
              </>
            )}
          </Button>
          <span className="text-xs text-muted-foreground">
            {previewState.generating
              ? 'Preparing preview in background...'
              : previewState.blobUrl
                ? 'Preview ready - click to download'
                : 'Preview merged disclosure package'}
          </span>
        </div>
        {previewState.error && (
          <div className="text-sm text-red-600">
            Preview error: {previewState.error}
          </div>
        )}
        {previewState.failures && previewState.failures.length > 0 && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-medium text-amber-800">
                  {previewState.failures.length} document{previewState.failures.length > 1 ? 's' : ''} could not be included in preview
                </div>
                <div className="text-xs text-amber-700 mt-1 space-y-1">
                  {previewState.failures.slice(0, 3).map((f, idx) => {
                    const doc = documentOrder?.documents?.find(d => d.id === f.documentId);
                    return (
                      <div key={idx}>
                        • {doc?.name || f.documentId}: {f.error}
                      </div>
                    );
                  })}
                  {previewState.failures.length > 3 && (
                    <div>... and {previewState.failures.length - 3} more</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Warning */}
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <div className="text-sm text-amber-800">
            <div className="font-medium">This action cannot be undone</div>
            <div>
              Sending will create a Disclosure Tracking entry in Encompass and send an email notification to the borrower.
            </div>
          </div>
        </div>

        {/* Success State */}
        {sendSuccess && (
          <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <div className="text-sm text-green-800 font-medium">
              Disclosures sent successfully! The borrower will receive an email shortly.
            </div>
          </div>
        )}
      </div>
    );
  };

  // Determine if we can proceed
  // User can proceed if a plan code is selected/applied and audit passed
  // Note: auditResult.success now properly accounts for blocking errors (severity: 'Error')
  const canProceedFromLoanInfo = selectedPlanCodeId && auditResult?.success;
  const canProceedFromDocuments = selectedDocIds.size > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Send Initial Disclosures - {loanNumber}
          </DialogTitle>
          <DialogDescription>
            Audit, generate, and send disclosure documents to {borrowerName}
          </DialogDescription>
        </DialogHeader>

        {/* Error Display */}
        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div className="flex-1 space-y-2">
              <div>{error}</div>
              {staleRecipientEmails.length > 0 && (
                <>
                  <div className="text-xs text-red-700">
                    Stale recipient{staleRecipientEmails.length > 1 ? 's' : ''}: {staleRecipientEmails.join(', ')}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={regenerateDisclosurePackage}
                    disabled={loading || sending}
                    className="border-red-300 text-red-800 hover:bg-red-100"
                  >
                    <RefreshCw className="h-3.5 w-3.5 mr-1" />
                    Regenerate
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={step} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="loan-info" disabled={loading}>
              1. Loan Info
            </TabsTrigger>
            <TabsTrigger value="documents" disabled={!canProceedFromLoanInfo || loading}>
              2. Documents
            </TabsTrigger>
            <TabsTrigger value="review" disabled={!canProceedFromDocuments || loading}>
              3. Review
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-auto py-4">
            <TabsContent value="loan-info" className="mt-0 h-full">
              {renderLoanInfo()}
            </TabsContent>

            <TabsContent value="documents" className="mt-0 h-full">
              {renderDocuments()}
            </TabsContent>

            <TabsContent value="review" className="mt-0 h-full">
              {renderReview()}
            </TabsContent>
          </div>
        </Tabs>

        {/* Footer Actions */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div>
            {step !== 'loan-info' && (
              <Button
                variant="ghost"
                onClick={() => setStep(step === 'review' ? 'documents' : 'loan-info')}
                disabled={loading || sending}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
              Cancel
            </Button>

            {step === 'loan-info' && (
              <Button
                onClick={goToDocuments}
                disabled={!canProceedFromLoanInfo || loading}
              >
                Next: Select Documents
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}

            {step === 'documents' && (
              <Button
                onClick={goToReview}
                disabled={!canProceedFromDocuments || loading}
              >
                Next: Review
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}

            {step === 'review' && (
              <Button
                onClick={handleSend}
                disabled={sending || sendSuccess || staleRecipientEmails.length > 0}
                className="bg-green-600 hover:bg-green-700"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : sendSuccess ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Sent!
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Disclosures
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
